import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CoreComponent } from '../core/core.component.js';
import { DialogService } from '../dialog/index.js';
import { FlexRowGap, FlexSize } from '../flex/index.js';
import { IconType } from '../icons/index.js';
import { TooltipLocation } from '../tooltip/tooltip.directive.js';
import { ButtonType } from './button.component.js';
import { ButtonMode, IButton } from './button.models.js';

@Component({
	selector: 'lib-button-list',
	templateUrl: './button-list.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ButtonListComponent extends CoreComponent {
	readonly buttons = input.required<readonly IButton[]>();
	readonly mode = input<ButtonMode>('auto');
	readonly extendListThreshold = input<number>();
	readonly useFullSizeButtons = input<boolean>();
	readonly addExtendedListMoreLabel = input<boolean>(false);
	readonly moreIcon = input<IconType>('more');
	readonly singleButtonTooltip = input<string>('More');
	readonly singleButtonType = input<ButtonType>();
	readonly singleButtonTooltipLocation = input<TooltipLocation>();

	public readonly showExtendedListMorelabel = computed(() => {
		if (!this.addExtendedListMoreLabel()) {
			return false;
		}
		if (this.calculatedMode() !== 'extendedList') {
			return false;
		}
		if (this.extendedListButtons().singleButtons.length) {
			return true;
		}
		return false;
	});

	public filteredButtonsSignal = computed<IButton[]>(() => {
		return this.buttons().filter((m) => {
			if (m.hidden?.() === true) {
				return false;
			}
			return true;
		});
	});

	public readonly calculatedMode = computed<ButtonMode>(() => {
		const mode = this.mode();

		if (mode === 'auto') {
			return 'extendedList';
		}

		if (mode === 'extendedList') {
			return 'extendedList';
		}
		if (mode === 'single') {
			return 'single';
		}

		if (this.isMobileView() && mode === 'list' && !this.isIconOnlyButtons()) {
			return 'extendedList';
		}

		return mode;
	});

	public readonly listButtons = computed<IButton[]>(() => {
		const buttons = this.filteredButtonsSignal();
		return buttons;
	});

	public readonly extendedListButtons = computed<{
		primaryButtons: IButton[];
		singleButtons: IButton[];
	}>(() => {
		const threshold: number = this.extendListThreshold() ?? 2;
		const filteredButtons = this.singleButtons();
		const nestedButtons: IButton[] = [];
		const primaryButtons: IButton[] = [];

		if (!filteredButtons.length) {
			return {
				primaryButtons: [],
				singleButtons: []
			};
		}

		if (filteredButtons.length <= threshold) {
			return {
				primaryButtons: filteredButtons.slice(0, threshold),
				singleButtons: []
			};
		}

		let index: number = 0;

		for (const filteredButton of filteredButtons) {
			if (index >= threshold) {
				nestedButtons.push(filteredButton);
			} else {
				primaryButtons.push(filteredButton);
			}
			index++;
		}

		return {
			primaryButtons: primaryButtons,
			singleButtons: nestedButtons
		};
	});

	public readonly singleButtons = computed<IButton[]>(() => {
		const buttons: IButton[] = [];

		for (const button of this.filteredButtonsSignal()) {
			// flatten nested buttons
			if (button.nestedButtons) {
				for (const nestedButton of button.nestedButtons) {
					const newNestedButton: IButton = {
						...nestedButton
					};

					// set button mode automatically if confirm is used
					if (nestedButton.confirm?.confirmMode === 'delete') {
						newNestedButton.buttonType = 'delete';
					}
					buttons.push(newNestedButton);
				}
			} else {
				buttons.push(button);
			}
		}
		return buttons;
	});

	public readonly buttonsListRowGap = computed<FlexRowGap>(() => {
		const iconOnlyList: boolean = this.isIconOnlyButtons();

		if (iconOnlyList) {
			return '0px';
		}

		return '16px';
	});

	public readonly extendedButtonsListRowGap = computed<FlexRowGap>(() => {
		const iconOnlyList: boolean = this.isIconOnlyButtons();

		if (iconOnlyList) {
			return '0px';
		}

		return '8px';
	});

	private readonly isIconOnlyButtons = computed<boolean>(() => {
		for (const button of this.buttons()) {
			if (!button.iconOnly) {
				return false;
			}
		}
		return true;
	});

	public readonly buttonColumnFlexSize = computed<FlexSize | undefined>(() => {
		if (this.isMobileView() || this.useFullSizeButtons()) {
			return 'fill';
		}

		return undefined;
	});

	constructor(private dialogService: DialogService) {
		super();
	}

	trackByButton(index: number, button: IButton): string {
		return `${button.title}_${button.icon}_${button.tooltip}`;
	}

	handleButtonClick(event: MouseEvent, button: IButton): void {
		if (button.disabled?.()) {
			return;
		}

		if (!button.confirm) {
			if (button.click) {
				button.click(event);
			}

			if (button.clickAsync) {
				super.subscribeToObservable(button.clickAsync(event));
			}
		} else {
			this.dialogService.openConfirmationDialog({
				mode: button.confirm.confirmMode,
				text: button.confirm.text,
				title: button.confirm.title,
				confirm: () => {
					button?.click?.(event);

					if (button.clickAsync) {
						super.subscribeToObservable(button.clickAsync(event));
					}
				}
			});
		}
	}

	getTooltip(button: IButton): string | undefined {
		if (button.tooltip) {
			return button.tooltip;
		}

		if (button.iconOnly && button.title) {
			return button.title;
		}

		if (button.nestedButtons?.length) {
			return 'More';
		}

		return undefined;
	}
}
